import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { IAssets, UpdateAssetsRequestDto, isCrypto } from '@sparling/financial-common'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import {
  updateAssetRequest,
  getClientTypesForAssignToUserRequest,
  getFireblocksAssetBySymbolRequest,
  clearFireblocksByTarget,
  clearFireblocksAll,
} from '../../../../../store/actions'
import { Modal } from '../../../../../components/Modal'
import {
  clientTypesForAssignToUserLoading,
  getClientTypesForAssignToUserEnum,
  updatePairModal,
  getFireblocksBaseOptions,
  getFireblocksQuoteOptions,
  getFireblocksBaseLoading,
  getFireblocksQuoteLoading,
} from '../../../../../store/selectors'
import { MultiSelect } from '../../../../../components/MultiSelect'
import { FireblocksSelect } from '../../../../../components/FireblocksSelect'

const useStyles = makeStyles((theme: Theme) => ({
  pairLabel: {
    fontSize: 14,
    paddingBottom: theme.spacing(1),
    opacity: 0.6,
  },
  pair: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: '1px solid rgba(255,255,255,0.6)',
    borderRadius: 4,
    color: 'rgba(255,255,255,1)',
    '&:hover': {
      border: '1px solid rgba(255,255,255,1)',
    },
  },
}))

interface PairsUpdateModalProps {
  isOpen: boolean
  modalClose: () => void
  pair?: IAssets | null
  clearCheckedIds: () => void
}

export const PairsUpdateModal: FC<PairsUpdateModalProps> = ({ isOpen, modalClose, pair, clearCheckedIds }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const loadingClientTypesForAssignToUser = useSelector(clientTypesForAssignToUserLoading)
  const clientTypesForAssignToUser = useSelector(getClientTypesForAssignToUserEnum)
  const { ids } = useSelector(updatePairModal)

  const getClientTypes = useCallback(() => {
    dispatch(getClientTypesForAssignToUserRequest({ search: '' }))
  }, [dispatch])

  const formMethods = useForm<UpdateAssetsRequestDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(UpdateAssetsRequestDto),
    defaultValues: {
      ids: [],
      fireblockBaseAssetsId: null as unknown as string,
      fireblockQuoteAssetsId: null as unknown as string,
    },
  })

  const {
    handleSubmit,
    reset,
    formState: { isValid },
    control,
    setValue,
    watch,
    setError,
    clearErrors,
  } = formMethods

  useEffect(() => {
    setValue('ids', ids ?? [])
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const clientTypeIds = pair?.clientTypes?.map((ct: { id: string }) => ct.id) ?? []
    const values = clientTypesForAssignToUser.filter((opt) => clientTypeIds.includes(opt.value)).map((opt) => opt.value)

    setValue('clientType', values)
    if (pair?.fireblockBaseAssetId) setValue('fireblockBaseAssetsId', pair.fireblockBaseAssetId)
    if (pair?.fireblockQuoteAssetId) setValue('fireblockQuoteAssetsId', pair.fireblockQuoteAssetId)
  }, [ids, pair, clientTypesForAssignToUser, setValue])

  const [baseSymbol, quoteSymbol] = useMemo((): [string | null, string | null] => {
    const u = pair?.underlier?.toString().trim().toUpperCase() || ''
    if (!u) return [null, null]
    if (u.includes('/')) {
      const parts = u.split('/')
      if (parts.length === 2) return [parts[0].trim(), parts[1].trim()]
      return [null, null]
    }
    if (u.length === 6) return [u.slice(0, 3), u.slice(3)]
    return [null, null]
  }, [pair])

  const baseIsCrypto = useMemo(() => (baseSymbol ? isCrypto(baseSymbol) : false), [baseSymbol])
  const quoteIsCrypto = useMemo(() => (quoteSymbol ? isCrypto(quoteSymbol) : false), [quoteSymbol])

  const baseAssetOptions = useSelector(getFireblocksBaseOptions)
  const quoteAssetOptions = useSelector(getFireblocksQuoteOptions)
  const baseAssetsLoading = useSelector(getFireblocksBaseLoading)
  const quoteAssetsLoading = useSelector(getFireblocksQuoteLoading)

  useEffect(() => {
    if (baseIsCrypto && baseSymbol) {
      dispatch(getFireblocksAssetBySymbolRequest({ symbol: baseSymbol, target: 'base' }))
    } else {
      dispatch(clearFireblocksByTarget({ target: 'base' }))
    }
  }, [dispatch, baseIsCrypto, baseSymbol])

  useEffect(() => {
    if (quoteIsCrypto && quoteSymbol) {
      dispatch(getFireblocksAssetBySymbolRequest({ symbol: quoteSymbol, target: 'quote' }))
    } else {
      dispatch(clearFireblocksByTarget({ target: 'quote' }))
    }
  }, [dispatch, quoteIsCrypto, quoteSymbol])

  const baseSelectedId = (watch('fireblockBaseAssetsId') ?? null) as string | null
  const quoteSelectedId = (watch('fireblockQuoteAssetsId') ?? null) as string | null

  const onSubmit = useCallback(
    (data) => {
      if (baseIsCrypto && quoteIsCrypto) {
        let hasError = false
        if (!baseSelectedId) {
          // eslint-disable-next-line
          setError('fireblockBaseAssetsId' as any, { type: 'required', message: 'Base asset is required' })
          hasError = true
        } else {
          // eslint-disable-next-line
          clearErrors('fireblockBaseAssetsId' as any)
        }
        if (!quoteSelectedId) {
          // eslint-disable-next-line
          setError('fireblockQuoteAssetsId' as any, { type: 'required', message: 'Quote asset is required' })
          hasError = true
        } else {
          // eslint-disable-next-line
          clearErrors('fireblockQuoteAssetsId' as any)
        }
        if (hasError) return
      }

      dispatch(updateAssetRequest(data))
      reset()
      dispatch(clearFireblocksAll())
      clearCheckedIds()
      modalClose()
    },
    [
      dispatch,
      reset,
      modalClose,
      clearCheckedIds,
      baseIsCrypto,
      quoteIsCrypto,
      baseSelectedId,
      quoteSelectedId,
      setError,
      clearErrors,
    ],
  )

  return (
    <Modal
      title={'Update Pair'}
      submitTitle="Update"
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!isValid || (baseIsCrypto && quoteIsCrypto && (!baseSelectedId || !quoteSelectedId))}
      isAutoWidth
    >
      <FormProvider {...formMethods}>
        <Grid container display={'flex'} justifyContent={'space-between'} minHeight={80}>
          {pair && (
            <Grid item xs={12}>
              <Typography className={classes.pairLabel} component="div">
                Pair
              </Typography>
              <Typography className={classes.pair} component="div">
                {pair.label}
              </Typography>
            </Grid>
          )}
          {baseIsCrypto && (
            <FireblocksSelect
              inputName={'fireblockBaseAssetsId'}
              labelName={'Fireblocks Base Asset'}
              placeholder={'Select Fireblocks Base Asset'}
              control={control}
              selectedId={baseSelectedId}
              loading={baseAssetsLoading}
              options={baseAssetOptions}
              required={baseIsCrypto}
              width={'100%'}
            />
          )}
          {quoteIsCrypto && (
            <FireblocksSelect
              inputName={'fireblockQuoteAssetsId'}
              labelName={'Fireblocks Quote Asset'}
              placeholder={'Select Fireblocks Quote Asset'}
              control={control}
              selectedId={quoteSelectedId}
              loading={quoteAssetsLoading}
              options={quoteAssetOptions}
              required={quoteIsCrypto}
              width={'100%'}
            />
          )}
          <MultiSelect
            inputName={'clientType'}
            labelName={'Client Type'}
            placeholder={'Select client type'}
            control={control}
            loading={loadingClientTypesForAssignToUser}
            options={clientTypesForAssignToUser}
            firstLoad={getClientTypes}
          />
        </Grid>
      </FormProvider>
    </Modal>
  )
}
